/** @jsxImportSource @emotion/react */
import React from "react";
import * as lodash from "lodash";
import {
  Input,
  Space,
  Switch,
  Table,
  Image,
  TableColumnsType,
  RadioChangeEvent,
  Popover,
  Checkbox,
  Button,
  Radio,
  notification,
  Form,
  InputNumber,
  Tooltip,
  Typography,
  Row,
  Col,
} from "antd";
import {
  CloseCircleOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  FileImageOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { LayoutContent } from "../../../components";
import {
  ProductUsecase,
  useProductDispatch,
  useProductState,
} from "../../../usecases/Product";
import { ProductColumns } from "../../../usecases/Product/usecase.d";
import * as Styles from "./styles";
import { TenantUsecase, useTenantDispatch } from "../../../usecases/Tenant";
import { useParams } from "react-router-dom";
import { RouteInfos } from "../../../common/routeInfo";
import { SettingUsecase, useSettingDispatch } from "../../../usecases/Setting";
import { GetProductDto } from "../../../services/Product/dto/get.dto";
import { CheckboxChangeEvent, CheckboxOptionType } from "antd/lib/checkbox";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  PointerSensor,
  UniqueIdentifier,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import dayjs from "dayjs";
import { useAuthState } from "../../../usecases/Auth";
import { ColumnFilterItem, TableRowSelection } from "antd/lib/table/interface";
import { Images } from "../../../common/images";
import { GetListParams } from "../../../services/Product/product.repository.d";

const titleKeys = {
  productThumbnailUrl: "Thumbnail",
  productKey: "Key",
  name: "Name",
  productPrice: "Price",
  productWidth: "Width",
  productHeight: "Height",
  productDepth: "Depth",
  productCategories: "Category",
  productRooms: "Room",
  productSurfaces: "Surface",
  productManufacturer: "Manufacturer",
  homeNames: "Homes",
  updatedByFullName: "Modified By",
  updatedAt: "Date Modified",
  isActive: "Status",
};

interface TenantPathParams {
  id: string;
}

enum ProductMode {
  View,
  Edit,
}

enum InputType {
  Text,
  Number,
  Switch,
}

const columnIds = {
  productThumbnailUrl: "0",
  productKey: "1",
  name: "2",
  productPrice: "3",
  productWidth: "4",
  productHeight: "5",
  productDepth: "6",
  productCategories: "7",
  productRooms: "8",
  productSurfaces: "9",
  productManufacturer: "10",
  homeNames: "11",
  updatedByFullName: "12",
  updatedAt: "13",
  isActive: "14",
};

interface HeaderCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
}

interface BodyCellProps extends React.HTMLAttributes<HTMLTableCellElement> {
  id: string;
  editing: boolean;
  inputType: InputType;
  dataIndex: string;
  record: GetProductDto;
}

interface DragIndexState {
  active: UniqueIdentifier;
  over: UniqueIdentifier | undefined;
  direction?: "left" | "right";
}

const DragIndexContext = React.createContext<DragIndexState>({
  active: -1,
  over: -1,
});

const dragActiveStyle = (dragState: DragIndexState, id: string) => {
  const { active, over } = dragState;
  // drag active style
  let style: React.CSSProperties = {};
  if (active && active === id) {
    style = { backgroundColor: "#E7E7E7", opacity: 0.7 };
  } else if (over && id === over && active !== over) {
    style = { borderInlineStart: "1px dashed gray" };
  }
  return style;
};

const TableHeaderCell: React.FC<HeaderCellProps> = (props) => {
  const dragState = React.useContext(DragIndexContext);
  // eslint-disable-next-line react/prop-types
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    // eslint-disable-next-line react/prop-types
    id: props.id,
  });
  const style: React.CSSProperties = {
    // eslint-disable-next-line react/prop-types
    ...props.style,
    cursor: "move",
    ...(isDragging
      ? { position: "relative", zIndex: 9999, userSelect: "none" }
      : {}),
    // eslint-disable-next-line react/prop-types
    ...dragActiveStyle(dragState, props.id),
  };
  return (
    <th
      {...props}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    />
  );
};

const TableBodyCell: React.FC<BodyCellProps> = (props: BodyCellProps) => {
  const dragState = React.useContext<DragIndexState>(DragIndexContext);
  const style = {
    ...props.style,
    ...dragActiveStyle(dragState, props.id),
  };
  const { dataIndex, record } = props;

  const inputType = React.useCallback((type: InputType) => {
    switch (type) {
      case InputType.Text:
        return <Input />;
      case InputType.Number:
        return <InputNumber />;
      case InputType.Switch:
        return <Switch />;
      default:
        return <></>;
    }
  }, []);

  const valuePropName = React.useCallback((type: InputType) => {
    switch (type) {
      case InputType.Text:
      case InputType.Number:
        return "value";
      case InputType.Switch:
        return "checked";
      default:
        return "";
    }
  }, []);

  const formName = `${dataIndex}_${record?.productKey}`;
  if (props.editing === true) {
    return (
      <td {...props} style={style}>
        <Form.Item
          name={formName}
          css={Styles.formInputStyle}
          valuePropName={valuePropName(props.inputType)}
        >
          {inputType(props.inputType)}
        </Form.Item>
      </td>
    );
  }
  return (
    <td
      {...props}
      // eslint-disable-next-line react/prop-types
      style={style}
    />
  );
};

interface ProductComponentProps {}

const ProductComponent: React.FC<ProductComponentProps> = () => {
  const [form] = Form.useForm();
  const productState = useProductState();
  const productDispatch = useProductDispatch();
  const tenantDispatch = useTenantDispatch();
  const settingDispatch = useSettingDispatch();
  const [editKeys, setEditKeys] = React.useState<React.Key[]>([]);

  const { id: tenantId } = useParams<TenantPathParams>();

  const authState = useAuthState();

  const [mode, setMode] = React.useState<ProductMode>(ProductMode.View);
  const [checkedRowKeys, setCheckedRowKeys] = React.useState<React.Key[]>([]);

  const [dragIndex, setDragIndex] = React.useState<DragIndexState>({
    active: -1,
    over: -1,
  });

  const [displayColumns, setDisplayColumns] = React.useState<ProductColumns>(
    {} as ProductColumns,
  );
  const [filter, setFilter] = React.useState<GetListParams>({});

  const [indeterminate, setIndeterminate] = React.useState(true);
  const [checkAll, setCheckAll] = React.useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      setColumns((prevState) => {
        const activeIndex = prevState.findIndex(
          (i) => columnIds[i.key] === active?.id,
        );
        const overIndex = prevState.findIndex(
          (i) => columnIds[i.key] === over?.id,
        );
        return arrayMove(prevState, activeIndex, overIndex);
      });
    }
    setDragIndex({ active: -1, over: -1 });
  };

  const onDragOver = ({ active, over }: DragOverEvent) => {
    const activeIndex = columns.findIndex(
      (i) => columnIds[i.key] === active.id,
    );
    const overIndex = columns.findIndex((i) => columnIds[i.key] === over?.id);
    setDragIndex({
      active: active.id,
      over: over?.id,
      direction: overIndex > activeIndex ? "right" : "left",
    });
  };

  const [thumbnailSize, setThumbnailSize] = React.useState("medium");

  const displayColumnLabels = React.useCallback(() => {
    const result = [];
    for (const property in displayColumns) {
      const value = displayColumns[property] as boolean;
      const label = titleKeys[property] as string;
      result.push({
        value,
        label,
      });
    }
    return result;
  }, [displayColumns]);

  const displayColumnOptions = React.useCallback((): CheckboxOptionType[] => {
    return displayColumnLabels().map((item) => item.label);
  }, [displayColumnLabels]);

  const displayColumnValues = React.useCallback((): CheckboxValueType[] => {
    const result = [];
    for (const property in displayColumns) {
      const value = displayColumns[property] as boolean;
      const label = titleKeys[property] as string;
      if (value) {
        result.push(label);
      }
    }
    return result;
  }, [displayColumns]);

  const thumbnailSizeValue = React.useCallback(() => {
    if (thumbnailSize === "small") return 50;
    else if (thumbnailSize === "medium") return 100;
    return 200;
  }, [thumbnailSize]);

  const isEditing = React.useCallback(() => {
    if (mode === ProductMode.Edit) return true;
    return false;
  }, [mode]);

  const renderThumbnailCell = React.useCallback(
    (value) => {
      if (value.productThumbnailUrl) {
        return (
          <Image
            width={thumbnailSizeValue()}
            height={thumbnailSizeValue()}
            src={value.productThumbnailUrl}
            fallback={Images.DEFAULT_THUMBNAIL}
            placeholder={
              <Image
                preview={false}
                src={Images.DEFAULT_THUMBNAIL}
                width={thumbnailSizeValue()}
                height={thumbnailSizeValue()}
              />
            }
          />
        );
      }
      return <></>;
    },
    [thumbnailSizeValue],
  );

  const renderTextTooltipCell = React.useCallback((value) => {
    return (
      <Tooltip title={value}>
        <Typography.Paragraph ellipsis css={Styles.singleTextLineStyle}>
          {value}
        </Typography.Paragraph>
      </Tooltip>
    );
  }, []);

  const renderArrayStringCell = React.useCallback((value) => {
    const newValue = value?.join(", ");
    return (
      <Tooltip title={newValue}>
        <Typography.Paragraph ellipsis css={Styles.singleTextLineStyle}>
          {newValue}
        </Typography.Paragraph>
      </Tooltip>
    );
  }, []);

  React.useEffect(() => {
    const listColumns = [];
    columns.forEach((column) => {
      if (column.title !== "Thumbnail") {
        listColumns.push({
          ...column,
        });
      } else {
        listColumns.push({
          ...column,
          render: renderThumbnailCell,
        });
      }
    });
    setColumns(listColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderThumbnailCell, thumbnailSize]);

  const onThumbnailSizeChange = React.useCallback((e: RadioChangeEvent) => {
    setThumbnailSize(e.target.value);
  }, []);

  const renderStatusCell = React.useCallback((value) => {
    return <Switch disabled checked={value.isActive} />;
  }, []);

  const renderUpdatedAt = React.useCallback((value) => {
    return dayjs(value).format("DD/MM/YYYY");
  }, []);

  const sorterProductThumbnailUrl = React.useCallback((a, b) => {
    if (a.productThumbnailUrl < b.productThumbnailUrl) return -1;
    if (a.productThumbnailUrl > b.productThumbnailUrl) return 1;
    return 0;
  }, []);

  const sorterProductKey = React.useCallback((a, b) => {
    if (a.productKey < b.productKey) return -1;
    if (a.productKey > b.productKey) return 1;
    return 0;
  }, []);

  const sorterProductName = React.useCallback((a, b) => {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  }, []);

  const sorterProductManufacturer = React.useCallback((a, b) => {
    if (a.productManufacturer < b.productManufacturer) return -1;
    if (a.productManufacturer > b.productManufacturer) return 1;
    return 0;
  }, []);

  const sorterProductPrice = React.useCallback((a, b) => {
    return a.productPrice - b.productPrice;
  }, []);

  const sorterProductWidth = React.useCallback((a, b) => {
    return a.productWidth - b.productWidth;
  }, []);

  const sorterProductHeight = React.useCallback((a, b) => {
    return a.productHeight - b.productHeight;
  }, []);

  const sorterProductDepth = React.useCallback((a, b) => {
    return a.productDepth - b.productDepth;
  }, []);

  const sorterCategories = React.useCallback((a, b) => {
    const sourceCategories = a.productCategories?.join(",");
    const targetCategories = b.productCategories?.join(",");
    if (sourceCategories < targetCategories) return -1;
    if (sourceCategories > targetCategories) return 1;
    return 0;
  }, []);

  const sorterRooms = React.useCallback((a, b) => {
    if (a.productRooms < b.productRooms) return -1;
    if (a.productRooms > b.productRooms) return 1;
    return 0;
  }, []);

  const sorterSurfaces = React.useCallback((a, b) => {
    if (a.sorterSurfaces < b.sorterSurfaces) return -1;
    if (a.sorterSurfaces > b.sorterSurfaces) return 1;
    return 0;
  }, []);

  const sorterHomeNames = React.useCallback((a, b) => {
    const sourceHomeNames = a.homeNames?.join(",") ?? "";
    const targetHomeNames = b.homeNames?.join(",") ?? "";
    if (sourceHomeNames < targetHomeNames) return -1;
    if (sourceHomeNames > targetHomeNames) return 1;
    return 0;
  }, []);

  const sorterUpdatedByFullName = React.useCallback((a, b) => {
    if (a.updatedByFullName < b.updatedByFullName) return -1;
    if (a.updatedByFullName > b.updatedByFullName) return 1;
    return 0;
  }, []);

  const sorterUpdatedAt = React.useCallback((a, b) => {
    if (a.updatedAt < b.updatedAt) return -1;
    if (a.updatedAt > b.updatedAt) return 1;
    return 0;
  }, []);

  const sorterStatus = React.useCallback((a) => {
    return a.isActive;
  }, []);

  const onStatusFilter = React.useCallback((value, record) => {
    return record.isActive === value;
  }, []);

  const onCategoriesFilter = React.useCallback((value, record) => {
    return record.productCategories?.some((val) => val === value);
  }, []);

  const onRoomsFilter = React.useCallback((value, record) => {
    return record.productRooms?.some((val) => val === value);
  }, []);

  const onSurfacesFilter = React.useCallback((value, record) => {
    return record.productSurfaces?.some((val) => val === value);
  }, []);

  const [categories, setCategories] = React.useState<ColumnFilterItem[]>([]);
  const [rooms, setRooms] = React.useState<ColumnFilterItem[]>([]);
  const [surfaces, setSurfaces] = React.useState<ColumnFilterItem[]>([]);

  const getListCategoryFilters = React.useCallback((data: GetProductDto[]) => {
    let result: Array<ColumnFilterItem> = [];
    data.forEach((item) => {
      item.productCategories?.forEach((category) => {
        const filterItem: ColumnFilterItem = {
          text: category === "" ? "None" : category,
          value: category,
        };
        if (
          result.filter((item) => item.text === filterItem.text).length === 0
        ) {
          result.push(filterItem);
        }
      });
    });
    result = result.sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    return result;
  }, []);

  const getListRoomFilters = React.useCallback((data: GetProductDto[]) => {
    let result: Array<ColumnFilterItem> = [];
    data.forEach((item) => {
      item.productRooms?.forEach((room) => {
        const filterItem: ColumnFilterItem = {
          text: room === "" ? "None" : room,
          value: room,
        };
        if (
          result.filter((item) => item.text === filterItem.text).length === 0
        ) {
          result.push(filterItem);
        }
      });
    });
    result = result.sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    return result;
  }, []);

  const getListSurfaceFilters = React.useCallback((data: GetProductDto[]) => {
    let result: Array<ColumnFilterItem> = [];
    data.forEach((item) => {
      item.productSurfaces?.forEach((surface) => {
        const filterItem: ColumnFilterItem = {
          text: surface === "" ? "None" : surface,
          value: surface,
        };
        if (
          result.filter((item) => item.text === filterItem.text).length === 0
        ) {
          result.push(filterItem);
        }
      });
    });
    result = result.sort((a, b) =>
      a.value < b.value ? -1 : a.value > b.value ? 1 : 0,
    );
    return result;
  }, []);

  React.useEffect(() => {
    const result = getListCategoryFilters(productState.data);
    setCategories(result);
  }, [getListCategoryFilters, productState.data]);

  React.useEffect(() => {
    const result = getListRoomFilters(productState.data);
    setRooms(result);
  }, [getListRoomFilters, productState.data]);

  React.useEffect(() => {
    const result = getListSurfaceFilters(productState.data);
    setSurfaces(result);
  }, [getListSurfaceFilters, productState.data]);
  const baseColumns = React.useCallback(
    (): TableColumnsType<GetProductDto> => [
      {
        key: "productThumbnailUrl",
        title: "Thumbnail",
        render: renderThumbnailCell,
        align: "left",
        sorter: sorterProductThumbnailUrl,
        onHeaderCell: () => ({ id: columnIds.productThumbnailUrl }),
        onCell: () => ({ id: columnIds.productThumbnailUrl }),
      },
      {
        key: "productKey",
        title: "Key",
        dataIndex: "productKey",
        align: "left",
        sorter: sorterProductKey,
        onHeaderCell: () => ({ id: columnIds.productKey }),
        onCell: () => ({ id: columnIds.productKey }),
      },
      {
        key: "name",
        title: "Name",
        dataIndex: "name",
        align: "left",
        render: renderTextTooltipCell,
        sorter: sorterProductName,
        onHeaderCell: () => ({ id: columnIds.name }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.name,
          record,
          dataIndex: "name",
        }),
      },
      {
        key: "productPrice",
        title: "Price",
        dataIndex: "productPrice",
        align: "left",
        sorter: sorterProductPrice,
        onHeaderCell: () => ({ id: columnIds.productPrice }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productPrice,
          record,
          dataIndex: "productPrice",
        }),
      },
      {
        key: "productWidth",
        title: "Width",
        dataIndex: "productWidth",
        align: "left",
        sorter: sorterProductWidth,
        onHeaderCell: () => ({ id: columnIds.productWidth }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productWidth,
          record,
          dataIndex: "productWidth",
        }),
      },
      {
        key: "productHeight",
        title: "Height",
        dataIndex: "productHeight",
        align: "left",
        sorter: sorterProductHeight,
        onHeaderCell: () => ({ id: columnIds.productHeight }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productHeight,
          record,
          dataIndex: "productHeight",
        }),
      },
      {
        key: "productDepth",
        title: "Depth",
        dataIndex: "productDepth",
        align: "left",
        sorter: sorterProductDepth,
        onHeaderCell: () => ({ id: columnIds.productDepth }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productDepth,
          record,
          dataIndex: "productDepth",
        }),
      },
      {
        key: "productCategories",
        title: "Category",
        dataIndex: "productCategories",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterCategories,
        onFilter: onCategoriesFilter,
        filters: categories,
        onHeaderCell: () => ({ id: columnIds.productCategories }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productCategories,
          record,
          dataIndex: "productCategories",
        }),
      },
      {
        key: "productRooms",
        title: "Room",
        dataIndex: "productRooms",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterRooms,
        onFilter: onRoomsFilter,
        filters: rooms,
        onHeaderCell: () => ({ id: columnIds.productRooms }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productRooms,
          record,
          dataIndex: "productRooms",
        }),
      },
      {
        key: "productSurfaces",
        title: "Surface",
        dataIndex: "productSurfaces",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterSurfaces,
        onFilter: onSurfacesFilter,
        filters: surfaces,
        onHeaderCell: () => ({ id: columnIds.productSurfaces }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productSurfaces,
          record,
          dataIndex: "productSurfaces",
        }),
      },
      {
        key: "productManufacturer",
        title: "Manufacturer",
        dataIndex: "productManufacturer",
        align: "left",
        render: renderTextTooltipCell,
        sorter: sorterProductManufacturer,
        onHeaderCell: () => ({ id: columnIds.productManufacturer }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.productManufacturer,
          record,
          dataIndex: "productManufacturer",
        }),
      },
      {
        key: "homeNames",
        title: "Homes",
        dataIndex: "homeNames",
        align: "left",
        render: renderArrayStringCell,
        sorter: sorterHomeNames,
        onHeaderCell: () => ({ id: columnIds.homeNames }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.homeNames,
          record,
          dataIndex: "homeNames",
        }),
      },
      {
        key: "updatedByFullName",
        title: "Modified By",
        dataIndex: "updatedByFullName",
        align: "left",
        render: renderTextTooltipCell,
        sorter: sorterUpdatedByFullName,
        onHeaderCell: () => ({ id: columnIds.updatedByFullName }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.updatedByFullName,
          record,
          dataIndex: "updatedByFullName",
        }),
      },
      {
        key: "updatedAt",
        title: "Date Modified",
        dataIndex: "updatedAt",
        align: "left",
        render: renderUpdatedAt,
        sorter: sorterUpdatedAt,
        onHeaderCell: () => ({ id: columnIds.updatedAt }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.updatedAt,
          record,
          dataIndex: "updatedAt",
        }),
      },
      {
        key: "isActive",
        title: "Status",
        render: renderStatusCell,
        filters: [
          {
            text: "Active",
            value: true,
          },
          {
            text: "Inactive",
            value: false,
          },
        ],
        sorter: sorterStatus,
        onFilter: onStatusFilter,
        onHeaderCell: () => ({ id: columnIds.isActive }),
        onCell: (record: GetProductDto) => ({
          id: columnIds.isActive,
          record,
          dataIndex: "isActive",
        }),
      },
    ],
    [
      categories,
      onCategoriesFilter,
      onRoomsFilter,
      onStatusFilter,
      onSurfacesFilter,
      renderArrayStringCell,
      renderStatusCell,
      renderTextTooltipCell,
      renderThumbnailCell,
      renderUpdatedAt,
      rooms,
      sorterCategories,
      sorterHomeNames,
      sorterProductDepth,
      sorterProductHeight,
      sorterProductKey,
      sorterProductManufacturer,
      sorterProductName,
      sorterProductPrice,
      sorterProductThumbnailUrl,
      sorterProductWidth,
      sorterRooms,
      sorterStatus,
      sorterSurfaces,
      sorterUpdatedAt,
      sorterUpdatedByFullName,
      surfaces,
    ],
  );

  const [columns, setColumns] = React.useState(() =>
    baseColumns().map((column, index) => ({
      ...column,
      onHeaderCell: () => ({ id: `${index}` }),
      onCell: () => ({ id: `${index}` }),
    })),
  );

  const setDisplayColumnVisibility = React.useCallback(
    (
      columnsVisibility: ProductColumns,
      categoryFilters: ColumnFilterItem[],
      roomFilters: ColumnFilterItem[],
      surfaceFilters: ColumnFilterItem[],
    ) => {
      setDisplayColumns(columnsVisibility);

      const columnLabels = [];
      for (const property in columnsVisibility) {
        if (columnsVisibility[property]) {
          columnLabels.push(titleKeys[property]);
        }
      }

      const newColumnList = columns.map((column) => {
        const columnItem = { ...column };
        if (columnLabels.includes(column.title as string)) {
          columnItem.width = "auto";
        } else {
          columnItem.width = "0px";
        }

        switch (columnItem.key) {
          case "productCategories":
            return {
              ...columnItem,
              filters: categoryFilters,
            };
          case "productRooms":
            return {
              ...columnItem,
              filters: roomFilters,
            };
          case "productSurfaces":
            return {
              ...columnItem,
              filters: surfaceFilters,
            };
          default:
            return columnItem;
        }
      });
      setColumns(newColumnList);
    },
    [columns],
  );

  const onDisplayColumnChanged = React.useCallback(
    (list: CheckboxValueType[]) => {
      if (productDispatch) {
        const productUsecase = new ProductUsecase(productDispatch);

        const newColumns: ProductColumns = {
          productThumbnailUrl: false,
          productKey: false,
          name: false,
          productPrice: false,
          productWidth: false,
          productHeight: false,
          productDepth: false,
          productCategories: false,
          productRooms: false,
          productSurfaces: false,
          productManufacturer: false,
          homeNames: false,
          updatedByFullName: false,
          updatedAt: false,
          isActive: false,
        };
        let columnLength = 0;
        for (const property in newColumns) {
          const label = titleKeys[property] as string;
          if (list.includes(label)) {
            newColumns[property] = true;
          }
          columnLength++;
        }
        productUsecase.setDisplayColumns(newColumns);
        setDisplayColumnVisibility(newColumns, categories, rooms, surfaces);
        setIndeterminate(!!list.length && list.length < columnLength);
        setCheckAll(list.length === columnLength);
      }
    },
    [categories, productDispatch, rooms, setDisplayColumnVisibility, surfaces],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onKeywordChangeHandle = React.useCallback(
    lodash.debounce((event) => {
      setFilter({
        ...filter,
        keyword: event.target.value,
      });
    }, 300),
    [],
  );

  const onCheckAllChange = React.useCallback(
    (e: CheckboxChangeEvent) => {
      if (productDispatch) {
        const newColumns: ProductColumns = {
          productThumbnailUrl: e.target.checked,
          productKey: e.target.checked,
          name: e.target.checked,
          productPrice: e.target.checked,
          productWidth: e.target.checked,
          productHeight: e.target.checked,
          productDepth: e.target.checked,
          productCategories: e.target.checked,
          productRooms: e.target.checked,
          productSurfaces: e.target.checked,
          productManufacturer: e.target.checked,
          homeNames: e.target.checked,
          updatedByFullName: e.target.checked,
          updatedAt: e.target.checked,
          isActive: e.target.checked,
        };
        const productUsecase = new ProductUsecase(productDispatch);
        productUsecase.setDisplayColumns(newColumns);
        setDisplayColumnVisibility(newColumns, categories, rooms, surfaces);
        setIndeterminate(false);
        setCheckAll(e.target.checked);
      }
    },
    [categories, productDispatch, rooms, setDisplayColumnVisibility, surfaces],
  );

  React.useEffect(() => {
    async function asyncLoadData() {
      if (productDispatch) {
        const productUsecase = new ProductUsecase(productDispatch);
        const productData = await productUsecase.getList(filter);
        const categoryFilters = getListCategoryFilters(productData);
        const roomFilters = getListRoomFilters(productData);
        const surfaceFilters = getListSurfaceFilters(productData);

        const columns = productUsecase.getDisplayColumns();
        setDisplayColumnVisibility(
          columns,
          categoryFilters,
          roomFilters,
          surfaceFilters,
        );
      }

      if (tenantDispatch) {
        const tenantUsecase = new TenantUsecase(tenantDispatch);
        await tenantUsecase.getList({});
      }
    }

    asyncLoadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productDispatch,
    tenantDispatch,
    filter,
    getListCategoryFilters,
    getListRoomFilters,
    getListSurfaceFilters,
  ]);

  React.useEffect(() => {
    let newColumns = [...columns];
    newColumns = newColumns.map((columnItem) => {
      switch (columnItem.key) {
        case "productCategories":
          return {
            ...columnItem,
            filters: categories,
          };
        case "productRooms":
          return {
            ...columnItem,
            filters: rooms,
          };
        case "productSurfaces":
          return {
            ...columnItem,
            filters: surfaces,
          };
        default:
          return columnItem;
      }
    });
    setColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, rooms, surfaces, productState.data]);

  React.useEffect(() => {
    async function asyncLoadDetail() {
      if (tenantDispatch) {
        const tenantUsecase = new TenantUsecase(tenantDispatch);
        const tenantItem = await tenantUsecase.get({ id: tenantId });

        if (settingDispatch) {
          const settingUsecase = new SettingUsecase(settingDispatch);
          settingUsecase.setMenuDetail({
            id: tenantItem.id,
            title: tenantItem.tenantName,
            routeInfo: RouteInfos.HomeBuilderProducts,
          });
        }
      }
    }
    asyncLoadDetail();
  }, [tenantId, tenantDispatch, settingDispatch]);

  React.useEffect(() => {
    const columnOnCells = {
      productThumbnailUrl: () => ({
        id: columnIds.productThumbnailUrl,
      }),
      productKey: () => ({
        id: columnIds.productKey,
      }),
      name: (record: GetProductDto) => ({
        id: columnIds.name,
        editing: isEditing(),
        inputType: InputType.Text,
        record,
        dataIndex: "name",
      }),
      productPrice: (record: GetProductDto) => ({
        id: columnIds.productPrice,
        editing: isEditing(),
        inputType: InputType.Number,
        record,
        dataIndex: "productPrice",
      }),
      productWidth: (record: GetProductDto) => ({
        id: columnIds.productWidth,
        editing: isEditing(),
        inputType: InputType.Number,
        record,
        dataIndex: "productWidth",
      }),
      productHeight: (record: GetProductDto) => ({
        id: columnIds.productHeight,
        editing: isEditing(),
        inputType: InputType.Number,
        record,
        dataIndex: "productHeight",
      }),
      productDepth: (record: GetProductDto) => ({
        id: columnIds.productDepth,
        editing: isEditing(),
        inputType: InputType.Number,
        record,
        dataIndex: "productDepth",
      }),
      productCategories: (record: GetProductDto) => ({
        id: columnIds.productCategories,
        editing: isEditing(),
        inputType: InputType.Text,
        record,
        dataIndex: "productCategories",
      }),
      productRooms: (record: GetProductDto) => ({
        id: columnIds.productRooms,
        record,
        dataIndex: "productRooms",
      }),
      productSurfaces: (record: GetProductDto) => ({
        id: columnIds.productSurfaces,
        record,
        dataIndex: "productSurfaces",
      }),
      productManufacturer: (record: GetProductDto) => ({
        id: columnIds.productManufacturer,
        editing: isEditing(),
        inputType: InputType.Text,
        record,
        dataIndex: "productManufacturer",
      }),
      updatedByFullName: (record: GetProductDto) => ({
        id: columnIds.updatedByFullName,
        record,
        dataIndex: "updatedByFullName",
      }),
      updatedAt: (record: GetProductDto) => ({
        id: columnIds.updatedAt,
        record,
        dataIndex: "updatedAt",
      }),
      isActive: (record: GetProductDto) => ({
        id: columnIds.isActive,
        editing: isEditing(),
        inputType: InputType.Switch,
        record,
        dataIndex: "isActive",
      }),
    };

    const newColumns = [];
    columns.forEach((column) => {
      newColumns.push({
        ...column,
        onCell: columnOnCells[column.key],
      });
    });
    setColumns(newColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  const openNotification = () => {
    const key = `open${Date.now()}`;
    const btn = (
      <Space>
        <Button
          type="primary"
          size="small"
          css={Styles.notificationButonStyle}
          onClick={() => notification.close(key)}
          icon={<div className="ico-check-white" style={{ marginRight: 10 }} />}
        >
          Confirm
        </Button>
      </Space>
    );
    notification.success({
      message: "Saved successfully",
      description: "Changes have been saved",
      btn,
      key,
      onClose: close,
      icon: <div className="ico-check-blue" css={Styles.iconSuccessStyle} />,
    });
  };

  const getUpdateValues = React.useCallback(() => {
    const fieldValues = form.getFieldsValue();
    const updateKeys = [];
    editKeys.forEach((key) => {
      const item = {
        productKey: key,
      };
      for (const property in fieldValues) {
        const dataIndexKeyPair = property.split("_");
        const dataIndex = dataIndexKeyPair[0];
        const keyValue = dataIndexKeyPair[1];
        if (keyValue === key) {
          if (dataIndex !== "productCategories") {
            item[dataIndex] = fieldValues[property];
          } else {
            item[dataIndex] = fieldValues[property]?.split(",") ?? [];
          }
        }
      }
      updateKeys.push(item);
    });
    return updateKeys;
  }, [editKeys, form]);

  const saveData = React.useCallback(async () => {
    const updateValues = getUpdateValues();
    if (updateValues.length === 0) return;

    const userId = authState.userInfo?.username;
    if (productDispatch) {
      const productUsecase = new ProductUsecase(productDispatch);
      await productUsecase.updateList({
        userId,
        items: updateValues,
      });
    }
    setEditKeys([]);
    setCheckedRowKeys([]);
    openNotification();
  }, [authState.userInfo?.username, getUpdateValues, productDispatch]);

  const onButtonEditClick = React.useCallback(async () => {
    const fieldValues: unknown = {};
    switch (mode) {
      case ProductMode.View:
        setMode(ProductMode.Edit);

        productState.data.forEach((item) => {
          fieldValues[`name_${item.productKey}`] = item.name;
          fieldValues[`productPrice_${item.productKey}`] = item.productPrice;
          fieldValues[`productWidth_${item.productKey}`] = item.productWidth;
          fieldValues[`productHeight_${item.productKey}`] = item.productHeight;
          fieldValues[`productDepth_${item.productKey}`] = item.productDepth;
          fieldValues[`productCategories_${item.productKey}`] =
            item.productCategories?.join(",");
          fieldValues[`productManufacturer_${item.productKey}`] =
            item.productManufacturer;
          fieldValues[`isActive_${item.productKey}`] = item.isActive;
        });
        form.setFieldsValue(fieldValues);
        break;
      case ProductMode.Edit:
        await saveData();
        setMode(ProductMode.View);
        break;
      default:
        break;
    }
  }, [form, mode, productState.data, saveData]);

  const onButtonCancelClick = React.useCallback(async () => {
    setEditKeys([]);
    setCheckedRowKeys([]);
    setMode(ProductMode.View);
  }, []);

  const onRowSelectionChange = React.useCallback(
    (selectedRowKeys: React.Key[]) => {
      setCheckedRowKeys(selectedRowKeys);
    },
    [],
  );

  const rowSelection =
    React.useCallback((): TableRowSelection<GetProductDto> => {
      if (mode === ProductMode.Edit) {
        return {
          type: "checkbox",
          onChange: onRowSelectionChange,
        };
      }
      return null;
    }, [mode, onRowSelectionChange]);

  const onFormValueChanges = React.useCallback(
    (changesValue) => {
      const keys = editKeys;
      for (const property in changesValue) {
        const dataIndexKeyPair = property.split("_");
        const dataIndex = dataIndexKeyPair[0];
        const key = dataIndexKeyPair[1];
        if (keys.includes(key) === false) {
          keys.push(key);
        }

        if (checkedRowKeys.length > 1) {
          const fieldValues = {};
          checkedRowKeys.forEach((rowKey) => {
            if (keys.includes(rowKey) === false) {
              keys.push(rowKey);
            }
            fieldValues[`${dataIndex}_${rowKey}`] = changesValue[property];
          });
          form.setFieldsValue(fieldValues);
        }
      }
      setEditKeys(keys);
    },
    [checkedRowKeys, editKeys, form],
  );

  return (
    <LayoutContent
      title="PRODUCTS"
      iconPath={Images.PRODUCT_BLACK}
      toolbar={
        <Row>
          <Col span={18}>
            <Input.Search
              placeholder="search..."
              allowClear
              css={Styles.inputSearchStyle}
              style={{ width: 300, verticalAlign: "middle" }}
              size="large"
              onChange={onKeywordChangeHandle}
              enterButton
            />
            <Popover
              placement="bottomRight"
              trigger="hover"
              content={
                <>
                  <Checkbox
                    indeterminate={indeterminate}
                    onChange={onCheckAllChange}
                    checked={checkAll}
                  >
                    Select all items
                  </Checkbox>
                  <br />
                  <Checkbox.Group
                    options={displayColumnOptions()}
                    value={displayColumnValues()}
                    onChange={onDisplayColumnChanged}
                    css={Styles.checkboxGroupStyle}
                  />
                </>
              }
            >
              <Button
                size="large"
                style={{ marginLeft: 8 }}
                icon={<EyeInvisibleOutlined />}
              >
                Hide Column
              </Button>
            </Popover>
            <Popover
              placement="bottomRight"
              trigger="hover"
              content={
                <>
                  <Radio.Group
                    value={thumbnailSize}
                    onChange={onThumbnailSizeChange}
                  >
                    <Space direction="vertical">
                      <Radio value={"small"}>Small</Radio>
                      <Radio value={"medium"}>Medium</Radio>
                      <Radio value={"large"}>Large</Radio>
                    </Space>
                  </Radio.Group>
                </>
              }
            >
              <Button
                size="large"
                style={{ marginLeft: 8 }}
                icon={<FileImageOutlined />}
              >
                Thumbnail Size
              </Button>
            </Popover>
          </Col>
          <Col span={6} css={Styles.flexEndStyle}>
            <Button
              size="large"
              style={{ marginLeft: 8 }}
              onClick={onButtonEditClick}
              className={mode === ProductMode.Edit ? "active" : ""}
              type={isEditing() ? "primary" : "default"}
              icon={isEditing() ? <SaveOutlined /> : <EditOutlined />}
            >
              {isEditing() ? "Save" : "Edit"}
            </Button>
            {isEditing() ? (
              <Button
                size="large"
                style={{ marginLeft: 8 }}
                onClick={onButtonCancelClick}
                icon={<CloseCircleOutlined />}
              >
                Cancel
              </Button>
            ) : (
              <></>
            )}
          </Col>
        </Row>
      }
    >
      <DndContext
        sensors={sensors}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={onDragEnd}
        onDragOver={onDragOver}
        collisionDetection={closestCenter}
      >
        <SortableContext
          items={columns.map((i) => i.key)}
          strategy={horizontalListSortingStrategy}
        >
          <DragIndexContext.Provider value={dragIndex}>
            <Form
              form={form}
              component={false}
              onValuesChange={onFormValueChanges}
            >
              <Table<GetProductDto>
                rowKey="productKey"
                dataSource={productState.data}
                css={Styles.tableStyle}
                pagination={{ position: ["topRight"], disabled: isEditing() }}
                scroll={{ y: "calc(100vh - 215px - 200px)" }}
                rowSelection={rowSelection()}
                columns={columns}
                components={{
                  header: { cell: TableHeaderCell },
                  body: { cell: TableBodyCell },
                }}
              ></Table>
            </Form>
          </DragIndexContext.Provider>
        </SortableContext>
        <DragOverlay>
          <th
            style={{
              backgroundColor: "#E7E7E7",
              padding: 16,
              width: 300,
            }}
          >
            {
              columns[columns.findIndex((i) => i.key === dragIndex.active)]
                ?.title as React.ReactNode
            }
          </th>
        </DragOverlay>
      </DndContext>
    </LayoutContent>
  );
};

export const Product = React.memo(ProductComponent);
